import { find, first, flatten, mapValues } from 'lodash-es';
import { notNil } from '../../utils/typescriptUtils';
import { IPdpData, TUserSelections } from './types/PDP';
import {
  ADVANCE_ORDER_TAG,
  E_GIFT_CARD_SKU,
  EARLY_ACCESS_TAGS,
  HIDDEN_INVENTORY_TAG,
  PREORDER_TAG,
} from 'src/scripts/utils/constants';
import { isTagPresent } from 'src/scripts/utils/helpers';
import { CartData } from 'src/scripts/utils/cart';
import { SiteSettings } from '../../utils/environment';
import { ShippingCountryState } from '../../utils/shipping';
import { customerLocationState } from './components/StoreLocator/customerLocationState';

export const convertCamelCaseToTitle = (str: string) => {
  return str.replace(/([A-Z])/g, ' $1');
};

const ProductImageRegex = /.*\/(.*?_.*?_.*)\./; // for the moment only checks for styleId & colorCode

export const getProductImageParts = (url?: string) => {
  // better name than capturegroup for `W9074R_00590_b1_s1_a1_m19_750x`? imageuid?
  const [, capturegroup]: (string | undefined)[] = url?.match(ProductImageRegex) || []; // TS 4.1
  if (url && capturegroup) {
    const [styleId, colorCode, ...rest] = capturegroup.split('_');
    const imgAttributePrefixes = { sizeCode: 's', backgroundCode: 'b', modelCode: 'm' };
    const imgAttributes = mapValues(
      imgAttributePrefixes,
      (prefix) => rest.find((code) => code.toLowerCase().startsWith(prefix))?.toLowerCase() || null
    );

    // [REVISIT] We still support the curveIndicator "2" without letter prefix.
    if (!imgAttributes.sizeCode) {
      const legacyCurveIndicator = rest[1] === '2' ? rest[1] : null;
      imgAttributes.sizeCode = legacyCurveIndicator;
    }

    return { styleId, url, colorCode, ...imgAttributes };
  }
};

// -- better names? ProductImageMetadata, ProductImageWithMetadata, ImageMetadata, etc.
export type ProductImageParts = NonNullable<ReturnType<typeof getProductImageParts>>;

export const computeSetPreviewImages = (pdpData: IPdpData, userSelections: TUserSelections): string[] => {
  const colorCodeForFirstProduct = getProductImageParts(
    find(pdpData.products[0]?.variants, {
      selectionOptions: { Color: userSelections[0]?.Color },
    })?.imageUrl
  )?.colorCode;

  return pdpData.assets.images
    .map(getProductImageParts)
    .filter(notNil)
    .filter((item) => item.colorCode === colorCodeForFirstProduct)
    .map((item) => item.url);
};

export const computeUnselectedOptionName = (userSelections: TUserSelections) => {
  const flatOptions = flatten(userSelections.map((selection) => Object.entries(selection)))
    .filter(([, value]) => !value)
    .map(([name]) => name);
  return first(flatOptions);
};

export const productTypeOptionNameOverrides = new Map<string, string>([['Gift Card', 'Amount']]);

// ? temporary until "T900"; productOptionNameToLabelOverride
/**
 * Derives the label for product option names (using tags)
 * - ex: a product with `OptionLabel:2:Volume` will have it's `2nd option` name replaced with `Volume`
 * - mostly for wellness/beauty (until we solve T900)
 */
export const computeProductOptionLabel = ({
  optionName,
  optionNumber,
  productType,
  tags,
}: {
  optionName: string;
  productType?: string;
  optionNumber?: number;
  tags?: string[];
}): string => {
  let label = optionName;

  // override 'Size' option to 'Amount' if product type includes 'Gift Card', will show for physical and e-gift cards
  if (productType?.toLowerCase().includes('gift card') && optionName === 'Size') {
    label = 'Amount';
  }

  if (tags && optionNumber) {
    const labelOverridePolicyRegex = /OptionLabel:(\d):(.*)$/;
    const replacementLabel = tags
      .map((tag) => tag.match(labelOverridePolicyRegex))
      .filter(notNil)
      .map(([, optionNumberAsString, replacementLabel]) => ({ optionNumberAsString, replacementLabel }))
      .find(({ optionNumberAsString }) => Number(optionNumberAsString) === optionNumber)?.replacementLabel;
    if (replacementLabel) label = replacementLabel;
  }

  return label;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getGCMinMaxPrice = (productItem: any) => {
  const gcPrice: { priceMin: number; priceMax: number } = { priceMin: 0, priceMax: 0 };
  let variantsData = [];

  if (productItem?.productType === 'Gift Card') variantsData = productItem?.variants;
  else if (productItem?.product_code === E_GIFT_CARD_SKU) variantsData = productItem?.shopify_variants;

  if (variantsData.length > 1) {
    gcPrice.priceMin = Math.min(...variantsData.map((item: { price: string }) => parseFloat(item?.price)));
    gcPrice.priceMax = Math.max(...variantsData.map((item: { price: string }) => parseFloat(item?.price)));
  }

  return gcPrice;
};

const getStickyHeaderHeight = () => {
  const uniBannerWrapper = document.querySelector('.sticky-uni-banner');
  const mainNav = document.getElementById('shopify-section-main-nav');
  return (mainNav?.offsetHeight || 0) + (uniBannerWrapper?.offsetHeight || 0);
};

const scrollToElement = (element: HTMLElement | null) => {
  if (element) {
    const top = window.pageYOffset + (element.getBoundingClientRect()?.top || 0) - getStickyHeaderHeight();
    window.scrollTo({ behavior: 'smooth', top });
  }
};

export const scrollToFitAndDescription = (element: HTMLElement | null) => {
  if (element) {
    const top = window.pageYOffset + (element.getBoundingClientRect()?.top || 0) - getStickyHeaderHeight() - 85;
    window.scrollTo({ behavior: 'smooth', top });
  }
};

export const scrollToSizeSelector = () => scrollToElement(document.querySelector('.button-size-selector'));

export const scrollToNotifyField = (index: number = 0) => {
  const element = document.querySelector(`.notify-form-container-${index}`);
  const bounding = element?.getBoundingClientRect();
  const isElementInVieport =
    bounding &&
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight);
  element && !isElementInVieport && scrollToElement(element);
};

export const scrollToSimilarItems = () => {
  const element = document.querySelector('#SimilarItems');
  if (element) {
    const top = element.offsetTop - 70;
    window.scrollTo({ behavior: 'smooth', top });
  }
};

export const isEligibleForPreorder = (
  quantity: number,
  tags: string[] = [],
  estimatedShipDate: string | null | undefined
) => {
  if (quantity <= 0 || isTagPresent(tags, HIDDEN_INVENTORY_TAG) || !estimatedShipDate) return false;
  return isTagPresent(tags, PREORDER_TAG);
};

export const hasAdvanceOrderAndPreorderTags = (tags: string[] = []) => {
  return (
    isTagPresent(tags, ADVANCE_ORDER_TAG) &&
    isTagPresent(tags, PREORDER_TAG) &&
    !isTagPresent(tags, HIDDEN_INVENTORY_TAG)
  );
};

export const checkIfProductHasEarlyAccessTags = (tags: string[]) => {
  return EARLY_ACCESS_TAGS.some((item) => tags.includes(item));
};

export const checkIfProductIsRewardOrGWP = (tags: string[]) => {
  return (
    isTagPresent(tags, 'loyaltypointsredemption') ||
    isTagPresent(tags, 'gwp') ||
    isTagPresent(tags, 'loyalty:status:active')
  );
};

export const scrollToColorSwatches = () => scrollToElement(document.querySelector('#variant-selector'));

export const getFisOrBopisFeatureFlagStatus = () => {
  const selectedCountry = ShippingCountryState.countryCode.toUpperCase();

  const themeStatus = window.Shopify.theme?.role === 'main' ? 'published' : 'preview';
  const bopisAndFisConfigurations = SiteSettings?.bopisAndFisConfigurations?.[themeStatus];

  const bopisCountries = (bopisAndFisConfigurations?.bopis_countries || []).map((countryCode) =>
    countryCode.toUpperCase()
  );
  const fisCountries = (bopisAndFisConfigurations?.fis_countries || []).map((countryCode) => countryCode.toUpperCase());

  const isBopisEnabled = Boolean(bopisAndFisConfigurations?.bopis_enabled && bopisCountries.includes(selectedCountry));
  const isFisEnabled = Boolean(bopisAndFisConfigurations?.fis_enabled && fisCountries.includes(selectedCountry));

  return { isFisEnabled, isBopisEnabled };
};

export const getDestinationObject = (
  options: {
    isBopis?: boolean;
  } = {
    isBopis: true,
  }
) => {
  const { isBopis } = options;
  const locationState = customerLocationState;
  const coordinates = !isBopis || locationState.zip ? null : locationState.coordinates;
  // default value is set for SDD
  const currentZip = isBopis ? locationState.zip : locationState.sddZip;
  const countryCode = isBopis ? ShippingCountryState.countryCode : 'US';

  return {
    ...(currentZip ? { postal_code: currentZip } : {}),
    country: countryCode,
    ...(coordinates
      ? {
          latitude: parseFloat(coordinates.latitude),
          longitude: parseFloat(coordinates.longitude),
        }
      : {}),
  };
};

export const mapCartItemsToBopisAndSddParamItems = (cartData: CartData) => {
  const { giftItemsAndLoyaltyRewards, regularItems } = cartData;
  return [...giftItemsAndLoyaltyRewards, ...regularItems].map((item) => ({
    sku: item.sku,
    quantity: item.quantity,
    requires_shipping: item.requires_shipping,
    isGWP: item.properties?._isGWP || false,
  }));
};

export const isHandbagPDPTemplate = () => window.getSiteSettings().isHandbagPDPTemplate;

export const isStickyNavbar = () => {
  const navBar = document.querySelector('#shopify-section-main-nav');
  return navBar && getComputedStyle(navBar).position === 'sticky';
};
